import React, { useEffect, useState } from "react";
import { X, Send, Phone, MessageCircle, CheckCircle2, GraduationCap } from "lucide-react";
import { site, grades } from "../mock/mock";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { createEnquiry, logContactEvent } from "../lib/api";

const EMPTY = { parent: "", student: "", grade: "", phone: "", email: "" };
const SEEN = "kpr_enquiry_popup_seen";

export const openEnquiry = () => window.dispatchEvent(new Event("kpr:open-enquiry"));

export default function EnquiryModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  // open on demand
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("kpr:open-enquiry", handler);
    return () => window.removeEventListener("kpr:open-enquiry", handler);
  }, []);

  // auto-open once per browser session, after a short delay
  useEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem(SEEN) === "1";
    } catch {
      seen = false;
    }
    if (seen) return;
    const t = setTimeout(() => {
      setOpen(true);
      try {
        sessionStorage.setItem(SEEN, "1");
      } catch {
        /* ignore */
      }
    }, 9000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const esc = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, []);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = {};
    if (!form.parent.trim()) err.parent = "Required";
    if (!form.student.trim()) err.student = "Required";
    if (!form.grade) err.grade = "Select a grade";
    if (!/^[0-9+\-\s]{10,15}$/.test(form.phone.trim())) err.phone = "Enter a valid number";
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) err.email = "Enter a valid email";
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }

    setBusy(true);
    try {
      await createEnquiry({ ...form, message: "", source: `popup:${window.location.pathname}` });
      setDone(true);
      setForm(EMPTY);
      toast({ title: "Enquiry received", description: "Our admissions team will call you shortly." });
      setTimeout(() => {
        setOpen(false);
        setDone(false);
      }, 2600);
    } catch {
      toast({
        title: "Could not send just now",
        description: `Please call ${site.hotlines[0]} and we'll take your details.`,
      });
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const fieldStyle = (k) => ({
    borderColor: errors[k] ? "#B4453A" : "rgba(11,59,54,.16)",
    background: "#fff",
  });

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ background: "rgba(5,32,29,.75)", backdropFilter: "blur(4px)" }}
      onClick={() => setOpen(false)}
    >
      <div
        className="fade-up relative w-full max-w-[860px] overflow-hidden rounded-[1.25rem]"
        style={{ background: "var(--cream)", boxShadow: "0 50px 100px -50px rgba(0,0,0,.85)" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Admission enquiry"
      >
        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full"
          style={{ background: "rgba(251,247,240,.9)", color: "var(--teal-800)" }}
        >
          <X size={17} />
        </button>

        <div className="grid md:grid-cols-[0.85fr_1.15fr]">
          {/* Visual side */}
          <div className="relative hidden md:block">
            <img
              src="/campus/school-1.jpg"
              alt="The KPR Academy campus"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span
              className="absolute inset-0"
              style={{ background: "linear-gradient(160deg, rgba(5,32,29,.82), rgba(11,59,54,.65))" }}
            />
            <div className="relative flex h-full flex-col justify-between p-8">
              <div>
                <p className="eyebrow" style={{ color: "var(--gold-200)" }}>
                  Admissions 2026–27
                </p>
                <h3
                  className="font-display mt-4 text-[1.75rem] leading-tight"
                  style={{ color: "#FBF7F0", fontWeight: 300 }}
                >
                  Pre-KG to Grade VII on a 160+ acre campus
                </h3>
              </div>

              <div className="space-y-3">
                {site.hotlines.map((h) => (
                  <a
                    key={h}
                    href={`tel:${h.replace(/\s/g, "")}`}
                    onClick={() =>
                      logContactEvent({
                        channel: "call",
                        number: h.replace(/\s/g, ""),
                        display: h,
                        label: "enquiry popup",
                      })
                    }
                    className="flex items-center gap-2.5 text-[0.9rem]"
                    style={{ color: "rgba(251,247,240,.9)" }}
                  >
                    <Phone size={14} style={{ color: "var(--gold-400)" }} /> {h}
                  </a>
                ))}
                <a
                  href={`https://wa.me/${site.whatsapp}?text=${encodeURIComponent(
                    "Hello! I would like to enquire about admissions at The KPR Academy."
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    logContactEvent({
                      channel: "whatsapp",
                      number: site.whatsapp,
                      display: site.whatsappDisplay,
                      label: "enquiry popup",
                    })
                  }
                  className="flex items-center gap-2.5 text-[0.9rem]"
                  style={{ color: "rgba(251,247,240,.9)" }}
                >
                  <MessageCircle size={14} style={{ color: "var(--gold-400)" }} /> WhatsApp us
                </a>
              </div>
            </div>
          </div>

          {/* Form side */}
          <div className="p-7 sm:p-9">
            {done ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center text-center">
                <span
                  className="grid h-16 w-16 place-items-center rounded-full"
                  style={{ background: "var(--teal-100)", color: "var(--teal-700)" }}
                >
                  <CheckCircle2 size={28} />
                </span>
                <h3 className="font-display mt-6 text-[1.5rem]" style={{ color: "var(--teal-900)" }}>
                  Thank you!
                </h3>
                <p className="mt-3 max-w-[320px] text-[0.9rem]" style={{ color: "var(--muted-ink)" }}>
                  Your enquiry has reached our admissions desk. We will call you within one working day.
                </p>
              </div>
            ) : (
              <>
                <span
                  className="grid h-11 w-11 place-items-center rounded-xl"
                  style={{ background: "var(--teal-100)", color: "var(--teal-700)" }}
                >
                  <GraduationCap size={20} strokeWidth={1.7} />
                </span>
                <h3 className="font-display mt-5 text-[1.6rem] leading-tight" style={{ color: "var(--teal-900)" }}>
                  Admission Enquiry
                </h3>
                <p className="mt-2 text-[0.86rem]" style={{ color: "var(--muted-ink)" }}>
                  Share a few details and our team will call you with seat availability and fee details.
                </p>

                <form onSubmit={submit} className="mt-6 grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label className="text-[0.76rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                      Parent / Guardian name
                    </Label>
                    <Input
                      className="mt-1.5 h-11"
                      style={fieldStyle("parent")}
                      placeholder="Your name"
                      value={form.parent}
                      onChange={(e) => set("parent", e.target.value)}
                    />
                  </div>

                  <div>
                    <Label className="text-[0.76rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                      Student name
                    </Label>
                    <Input
                      className="mt-1.5 h-11"
                      style={fieldStyle("student")}
                      placeholder="Child's name"
                      value={form.student}
                      onChange={(e) => set("student", e.target.value)}
                    />
                  </div>

                  <div>
                    <Label className="text-[0.76rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                      Applying for
                    </Label>
                    <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
                      <SelectTrigger className="mt-1.5 h-11" style={fieldStyle("grade")}>
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {grades.map((g) => (
                          <SelectItem key={g} value={g}>
                            {g}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-[0.76rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                      Phone
                    </Label>
                    <Input
                      className="mt-1.5 h-11"
                      style={fieldStyle("phone")}
                      placeholder="+91 90000 00000"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                    />
                  </div>

                  <div>
                    <Label className="text-[0.76rem] font-semibold" style={{ color: "var(--teal-800)" }}>
                      Email
                    </Label>
                    <Input
                      className="mt-1.5 h-11"
                      style={fieldStyle("email")}
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                    />
                  </div>

                  {Object.keys(errors).length > 0 && (
                    <p className="text-[0.76rem] sm:col-span-2" style={{ color: "#B4453A" }}>
                      Please complete the highlighted fields correctly.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={busy}
                    className="group flex items-center justify-center gap-3 rounded-full py-3.5 text-[0.86rem] font-semibold sm:col-span-2"
                    style={{
                      background: busy ? "var(--gold-400)" : "var(--gold)",
                      color: "#fff",
                      cursor: busy ? "wait" : "pointer",
                      transition: "background .3s ease, transform .3s ease",
                    }}
                  >
                    {busy ? "Sending…" : "Request a Call Back"}
                    <Send size={15} className="group-hover:translate-x-1" style={{ transition: "transform .3s ease" }} />
                  </button>

                  <p className="text-center text-[0.7rem] sm:col-span-2" style={{ color: "var(--muted-ink)" }}>
                    We never share your details. Office hours: Mon – Sat, 9:00 AM to 4:00 PM.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
